import { BarChart3, Calendar, CalendarCheck, FileText, Inbox, ListTodo, MessageSquare, Settings, ShieldCheck, ShoppingCart, Users } from 'lucide-react'

// `feature` = subscription entitlement key (CLA-906). A nav item whose
// feature is missing from the clinic's plan stays VISIBLE but locked —
// hiding it would hide the best advertising surface the tier has. Click
// opens the upgrade panel instead of navigating.
export type NavItem = { to: string; label: string; icon: typeof FileText; feature?: string }

const allNavItems: NavItem[] = [
  { to: '/', label: 'Notes', icon: FileText },
  { to: '/tasks', label: 'Tasks', icon: ListTodo },
  { to: '/patients', label: 'Patients', icon: Users },
  { to: '/order', label: 'Order', icon: ShoppingCart, feature: 'store' },
  { to: '/messaging', label: 'Messages', icon: MessageSquare },
  { to: '/confirmations', label: 'Confirmations', icon: CalendarCheck, feature: 'booking' },
  { to: '/calendar', label: 'Calendar', icon: Calendar },
  { to: '/reports', label: 'Reports', icon: BarChart3, feature: 'analytics' },
  { to: '/inbox', label: 'Inbox', icon: Inbox },
  { to: '/safety', label: 'Safety', icon: ShieldCheck },
  { to: '/settings', label: 'Settings', icon: Settings },
]

// Roles whose day is the diary, not the note: front desk and the clinic
// admin. Practitioners (and owners, who are practitioner + org_admin) keep
// Notes first (CLA-1249).
const CALENDAR_FIRST_ROLES = ['clinic_staff', 'clinic_admin']

/**
 * The nav a given user actually gets. Order is only offered to someone the
 * server lets order for the clinic (`authorize_clinic_order`: platform admin
 * or a clinic/org manager) — a nurse clicking it got a page of 403s. Safety
 * stays for everyone: incident visibility is scoped server-side, so a nurse
 * or viewer sees the reports they made.
 *
 * Reports (CLA-1307) goes to anyone the registry would offer at least one
 * metric to — `sales:read` for the money reports, `settings:manage` for the
 * schedule ones. A nurse without either would get an empty page, so the
 * entry is withheld rather than locked: the padlock is for a plan gap, not
 * a permission gap.
 */
export function navItemsFor({
  canOrder,
  canReport = false,
  clinicRole,
}: {
  canOrder: boolean
  canReport?: boolean
  clinicRole: string | null
}): NavItem[] {
  const items = allNavItems.filter(
    (item) => (item.to !== '/order' || canOrder) && (item.to !== '/reports' || canReport),
  )
  if (clinicRole && CALENDAR_FIRST_ROLES.includes(clinicRole)) {
    const calendar = items.find((item) => item.to === '/calendar')!
    return [calendar, ...items.filter((item) => item !== calendar)]
  }
  return items
}
